
import React, { useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { Sample, Patient, AnalysisCatalogItem, Project } from '../types';
import { Plus, Search, ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';
import SampleTable from './samples/SampleTable';
import CreateSampleModal from './samples/CreateSampleModal';
import AnalysisRequestModal from './samples/AnalysisRequestModal';
import ResultValidationModal from './samples/ResultValidationModal';
import LabelModal from './samples/LabelModal';
import SampleReport from './samples/SampleReport';

const SampleList: React.FC = () => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [catalogItems, setCatalogItems] = useState<AnalysisCatalogItem[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [activeModal, setActiveModal] = useState<'create' | 'analysis' | 'result' | 'label' | 'report' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const fetchSamples = async () => {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data, error } = await supabase.from('samples').select('*, patients(*), projects(*)').order('created_at', { ascending: false });
      if (!error && data) setSamples(data as Sample[]);
    } else {
        setSamples([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSamples();
    if (isSupabaseConfigured()) {
        supabase.from('patients').select('*').then(({data}) => data && setPatients(data as Patient[]));
        supabase.from('analysis_catalog').select('*').order('name').then(({data}) => data && setCatalogItems(data as AnalysisCatalogItem[]));
        supabase.from('projects').select('*').order('name').then(({data}) => data && setProjects(data as Project[]));
    }
  }, []);

  const handleAction = (action: string, sample: Sample) => {
      setSelectedSample(sample);
      if (action === 'analyze') setActiveModal('analysis');
      else if (action === 'validate') setActiveModal('result');
      else if (action === 'label') setActiveModal('label');
      else if (action === 'report') setActiveModal('report');
  };

  const filteredSamples = samples.filter(s => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (s.patients ? `${s.patients.last_name} ${s.patients.first_name}`.toLowerCase() : '').includes(searchLower) || s.sample_type.toLowerCase().includes(searchLower);
    const matchesProject = filterProject === 'all' ? true : filterProject === 'none' ? !s.project_id : s.project_id === filterProject;
    return matchesSearch && matchesProject;
  });

  const currentSamples = filteredSamples.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredSamples.length / itemsPerPage);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      
      {/* HEADER FIXE */}
      <div className="p-4 md:p-8 pb-0 shrink-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <h2 className="text-2xl font-bold text-slate-800">Gestion des Prélèvements</h2>
          <button onClick={() => setActiveModal('create')} className="hidden md:flex bg-indigo-600 text-white px-4 py-2 rounded-lg items-center gap-2 hover:bg-indigo-700 transition-colors shadow-sm">
            <Plus size={18} /> Nouveau Prélèvement
          </button>
        </div>

        {/* BARRE DE FILTRES FIXE */}
        <div className="bg-white rounded-t-xl border border-slate-200 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input type="text" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} placeholder="Rechercher..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="relative min-w-[200px]">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <select value={filterProject} onChange={(e) => { setFilterProject(e.target.value); setCurrentPage(1); }} className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500">
                      <option value="all">Tous les projets</option>
                      <option value="none">Standard</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
              </div>
          </div>
          {filteredSamples.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600"><ChevronLeft size={20} /></button>
              <span className="text-sm font-medium px-2">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 text-slate-600"><ChevronRight size={20} /></button>
            </div>
          )}
        </div>
      </div>

      {/* ZONE TABLEAU SCROLLABLE */}
      <div className="flex-1 overflow-hidden px-4 md:px-8 pb-4">
        <div className="h-full bg-white border border-t-0 border-slate-200 shadow-sm rounded-b-xl overflow-auto relative">
           <SampleTable samples={currentSamples} onAction={handleAction} />
        </div>
      </div>

      <button onClick={() => setActiveModal('create')} className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-indigo-600 text-white rounded-full shadow-lg flex items-center justify-center z-40 transition-transform active:scale-95"><Plus size={32} /></button>

      {activeModal === 'create' && <CreateSampleModal onClose={() => setActiveModal(null)} onSampleCreated={fetchSamples} patients={patients} fetchPatients={() => {}} />}
      {activeModal === 'analysis' && selectedSample && <AnalysisRequestModal sample={selectedSample} catalogItems={catalogItems} onClose={() => setActiveModal(null)} onConfirm={fetchSamples} />}
      {activeModal === 'result' && selectedSample && <ResultValidationModal sample={selectedSample} catalogItems={catalogItems} onClose={() => setActiveModal(null)} onSuccess={fetchSamples} />}
      {activeModal === 'label' && selectedSample && <LabelModal sample={selectedSample} onClose={() => setActiveModal(null)} onPrint={() => window.print()} />}
      {activeModal === 'report' && selectedSample && <SampleReport selectedSample={selectedSample} onClose={() => setActiveModal(null)} onPrint={() => window.print()} />}
    </div>
  );
};

export default SampleList;
